import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import * as Util from '../../../common/utilities';
import { withSession } from '../../../i18n/containers/AppInit.js';

import { Wizard, WizardPage } from '../../../common/components';
import { NoAttributes } from '../../../ecs/components';
import {
  ExperimentEditImage,
  ExperimentEditInfo,
  ExperimentEditSetting,
  ExperimentEditReview
} from '../';

import './ExperimentEditWizard.scss';

class ExperimentEditWizard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dirtyExperiment: false,
      imageMinimum:
        this.props.modals?.experimentWizard?.data?.experiment
          ?.content_test_assets?.length < 2
    };
  }

  handlePropErrors(errors) {
    if (errors['imageSizeLimit'] || errors['imagePayloadLimit']) {
      if (errors['imageSizeLimit']) {
        this.props.actions.notifyApiError({
          apiError: { code: 'IMAGE_TOO_LARGE' }
        });
      } else {
        this.props.actions.notifyApiError({
          apiError: { code: 'IMAGE_PAYLOAD_TOO_LARGE' }
        });
      }
    }
  }

  componentWillUpdate(nextProps) {
    if (
      Util.propChanged(
        this.props,
        nextProps,
        'modals.experimentWizard.data.experiment'
      )
    ) {
      const selectables =
        nextProps.modals?.experimentWizard?.data?.experiment
          ?.content_test_assets || [];
      let selectableRows = [];
      if (selectables.length) {
        selectableRows = selectables.filter(function(selectable) {
          return selectable['_destroy'] !== true;
        });
      }
      const dirtyImages =
        JSON.stringify(selectables) !==
        JSON.stringify(
          nextProps.modals?.experimentWizard?.data?.loadedExperiment
            ?.content_test_assets
        );
      const dirtyTest =
        JSON.stringify(nextProps.modals?.experimentWizard?.data?.experiment) !==
        JSON.stringify(
          nextProps.modals?.experimentWizard?.data?.loadedExperiment
        );
      const isDirty = dirtyTest || dirtyImages;
      const imageMin = selectableRows?.length < 2;

      let emptyField = selectableRows.filter(function(selectable) {
        return selectable.name === '' || selectable.redirect_url === '';
      });
      let imagePayloadSize = 0;
      let imageSizeLimit = false;
      selectableRows.forEach(selectable => {
        let imageSize = 0;
        if (selectable.image_attributes?.image_data) {
          const base64str = selectable.image_attributes?.image_data?.split(
            'base64,'
          )[1]; //remove the image type metadata.
          imageSize = Buffer.from(base64str, 'base64').length; //encode image into bytes
        }
        if (imageSize > 1024 * 1024) {
          imageSizeLimit = true;
        }
        imagePayloadSize += imageSize;
      });
      const imageLimitExceeded = imagePayloadSize > 10 * 1024 * 1024;
      this.handlePropErrors({
        imageSizeLimit: imageSizeLimit,
        imagePayloadLimit: imageLimitExceeded
      });
      this.setState({
        dirtyExperiment: isDirty,
        imageMinimum: imageMin
      });
      this.props.actions.updateModal('experimentWizard', {
        saveDisabled: !isDirty,
        finishDisabled: imageMin,
        step1: {
          completed:
            !emptyField.length && !imageLimitExceeded && !imageSizeLimit
        }
      });
    }
  }

  handleSave(session) {
    return modal => {
      let { experiment, mode } = this.data();
      switch (mode) {
        case 'create':
          this.props.actions.createExperiment({ ...experiment }, 'overview', {
            cToken: session.experimentCsrfToken
          });
          break;
        case 'edit':
          this.props.actions.editExperiment(
            { ...experiment },
            this.props.context,
            { cToken: session.experimentCsrfToken }
          );
          break;
        default:
          break;
      }
      this.props.actions.closeModal(modal);
      this.handleClose();
    };
  }

  handleFinish(session) {
    return modal => {
      let { experiment, mode } = this.data();
      switch (mode) {
        case 'create':
          this.props.actions.createExperiment({ ...experiment }, 'overview', {
            status: 'PUBLISH',
            cToken: session.experimentCsrfToken
          });
          break;
        case 'edit':
          this.props.actions.editExperiment(
            { ...experiment },
            this.props.context,
            {
              status: 'PUBLISH',
              cToken: session.experimentCsrfToken
            }
          );
          break;
        default:
          break;
      }
      this.props.actions.closeModal(modal);
      this.handleClose();
    };
  }

  handleClose() {
    this.props.actions.searchExperiments(
      {},
      { page: 1 },
      { context: 'overview', mode: 'reset' }
    );
  }

  data() {
    return this.props.modals.experimentWizard?.data || {};
  }

  infoFieldChange(element, value) {
    const { experiment } = this.data();
    let update;
    experiment[element] = value;
    if (!experiment.engine_name) {
      experiment['engine_name'] = 'ABN';
      experiment['end_trigger'] = 'MANUAL';
    }

    if (element === 'name') {
      update = {
        experiment: { ...experiment },
        step0: { completed: true }
      };
    } else {
      update = {
        experiment: {
          ...experiment
        }
      };
    }
    update['finishDisabled'] = this.props.finishDisabled;
    this.props.actions.updateModal('experimentWizard', update);
  }

  settingsFieldChange(element, value) {
    const { experiment } = this.data();
    experiment[element] = value;
    const update = {
      experiment: {
        ...experiment
      }
    };
    this.props.actions.updateModal('experimentWizard', update);
  }

  imageFieldChange(selectable, element, value) {
    const experiment = this.props.modals?.experimentWizard?.data?.experiment;
    const index = selectable[0];

    experiment.content_test_assets[index][element] = value;
    const update = {
      experiment: {
        ...experiment
      }
    };
    this.props.actions.updateModal('experimentWizard', update);
  }

  renderNoAttributes() {
    return <NoAttributes intl={this.props.intl} attributeType="experiment" />;
  }

  pages() {
    const { mode, experiment, disabled } = this.data();

    const infoPage = (
      <WizardPage
        key="step0"
        step="step0"
        stepName="components.experiment.experimentWizard.step0.progressIndicator"
        required={!experiment.name}
        flex
        footer={['CANCEL', 'SAVE', 'NEXT']}
      >
        <ExperimentEditInfo
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          experiment={experiment}
          onFieldChange={this.infoFieldChange.bind(this)}
        />
      </WizardPage>
    );

    const imagePage = (
      <WizardPage
        key="step1"
        step="step1"
        stepName="components.experiment.experimentWizard.step1.progressIndicator"
        required={true}
        flex
        footer={['BACK', 'SAVE', 'NEXT']}
      >
        <ExperimentEditImage
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          experiment={experiment}
          experiments={this.props.experiments}
          canDisableExperiment={!disabled}
          assets={this.props.assets}
          contentBuilder={this.props.contentBuilder}
          type="image"
          onFieldChange={this.imageFieldChange.bind(this)}
        />
      </WizardPage>
    );

    const settingsPage = (
      <WizardPage
        key="step2"
        step="step2"
        stepName="components.experiment.experimentWizard.step2.progressIndicator"
        flex
        footer={['BACK', 'SAVE', 'NEXT']}
      >
        <ExperimentEditSetting
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          experiment={experiment}
          onFieldChange={this.settingsFieldChange.bind(this)}
        />
      </WizardPage>
    );

    const reviewPage = (
      <WizardPage
        key="step3"
        step="step3"
        stepName="components.experiment.experimentWizard.step3.progressIndicator"
        flex
        footer={
          experiment.status === 'DRAFT'
            ? ['BACK', 'SAVE', 'FINISH']
            : ['BACK', 'SAVE']
        }
      >
        <ExperimentEditReview
          actions={this.props.actions}
          modals={this.props.modals}
          mode={mode}
          experiment={experiment}
          canDisableExperiment={!disabled}
        />
      </WizardPage>
    );

    let pages = [infoPage, imagePage, settingsPage, reviewPage];
    return pages;
  }

  render() {
    if (!this.data()?.experiment) {
      return null;
    }
    const { mode } = this.data();
    const { formatMessage } = this.props.intl;

    const title = formatMessage({
      id: `components.experiment.experimentWizard.titles.${mode}`
    });

    return withSession(session => {
      return (
        <Wizard
          title={title}
          modalName="experimentWizard"
          containerClassName="experiment-wizard"
          size="small"
          onFinish={this.handleFinish(session)}
          onClose={this.handleClose.bind(this)}
          onSave={this.handleSave(session)}
          backLabel={formatMessage({ id: 'common.actions.previous' })}
          saveLabel={formatMessage({ id: 'common.actions.saveExit' })}
          finishLabel={formatMessage({ id: 'common.actions.publish' })}
          {...this.props}
        >
          {this.pages()}
        </Wizard>
      );
    });
  }
}

ExperimentEditWizard.propTypes = {
  actions: PropTypes.shape({
    closeModal: PropTypes.func.isRequired,
    updateModal: PropTypes.func.isRequired
  }).isRequired,
  assets: PropTypes.object,
  experiments: PropTypes.object,
  context: PropTypes.string,
  contentBuilder: PropTypes.object,
  modals: PropTypes.shape({
    experimentWizard: PropTypes.shape({
      data: PropTypes.shape({
        mode: PropTypes.oneOf(['create', 'edit']),
        experiment: PropTypes.object.isRequired,
        loadedExperiment: PropTypes.object,
        saveDisabled: PropTypes.bool,
        errors: PropTypes.object
      })
    })
  }).isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(ExperimentEditWizard);
